import React from 'react';

import { Box } from '@tanium/react-box';
import { TFunction } from '@tanium/react-i18n';
import { ThemeDefinition } from '@tanium/react-theme-definition';

import { useTranslation } from '../../localization';
import { ExtendAndOverride } from '../../utils/ExtendAndOverride';
import { SeverityType } from '../../utils/SeverityType';
import { Badge, BadgeProps } from './Badge';
import { BadgeInfoIcon } from './BadgeInfoIcon';
import { BadgeLabel } from './BadgeLabel';
import {
  AboveAverageFill,
  AboveAverageText,
  AverageFill,
  AverageText,
  BelowAverageFill,
  BelowAverageText,
  InformationalFill,
  InformationalText,
  NeedsImprovementFill,
  NeedsImprovementText,
  NeutralFill,
  NeutralText,
  OptimizedFill,
  OptimizedText,
  PendingFill,
  PendingText,
} from './themeDefinitions';
import { TooltipContent, TooltipContentProps } from './TooltipContent';

interface PropsWithType {
  /**
   * The severity type of the badge. This value informs the default label, background color, and
   * color.
   */
  type: SeverityType;
}

interface PropsWithCustom {
  /**
   * The background color of the badge. Defaults to a color based on the `type`. Must be provided if
   * `type` is omitted.
   */
  bg: ThemeDefinition;
  /**
   * The text color of the badge. Defaults to a color based on the `type`. Must be provided if
   * `type` is omitted.
   */
  color: ThemeDefinition;
  /**
   * The label to show inside the badge. e.g., "Needs Improvement". Defaults to a label based on the
   * `type`. Must be provided if `type` is omitted.
   */
  badgeLabel?: React.ReactNode;
}

type ConditionalProps = (PropsWithType & Partial<PropsWithCustom>) | (PropsWithCustom & Partial<PropsWithType>);

type OwnProps = ConditionalProps & TooltipContentProps;

export type ConditionalBadgeProps = ExtendAndOverride<OwnProps, Omit<BadgeProps, 'children'>>;

const typeMap = (t: TFunction): Record<SeverityType, [bg: ThemeDefinition, color: ThemeDefinition, label: string]> => ({
  optimized: [OptimizedFill, OptimizedText, t('Optimized')],
  aboveAverage: [AboveAverageFill, AboveAverageText, t('Above Average')],
  average: [AverageFill, AverageText, t('Average')],
  belowAverage: [BelowAverageFill, BelowAverageText, t('Below Average')],
  needsImprovement: [NeedsImprovementFill, NeedsImprovementText, t('Needs Improvement')],
  pending: [PendingFill, PendingText, t('Pending')],
  informational: [InformationalFill, InformationalText, t('Informational')],
  neutral: [NeutralFill, NeutralText, t('Neutral')],
});

export const ConditionalBadge = ({
  bg: bgProp,
  color: colorProp,
  badgeLabel: badgeLabelProp,
  itemColor,
  itemLabel,
  threshold,
  type,
  width,
  ...badgeProps
}: ConditionalBadgeProps): JSX.Element => {
  const { t } = useTranslation();
  const [typeBg, typeColor, typeLabel] = typeMap(t)[type ?? 'neutral'] ?? typeMap(t).neutral;

  const bg = bgProp ?? typeBg;
  const color = colorProp ?? typeColor;
  const badgeLabel = badgeLabelProp ?? typeLabel;

  const tooltipContent = <TooltipContent itemLabel={itemLabel} threshold={threshold} itemColor={itemColor} />;

  return (
    <Badge bg={bg} color={color} width={width} overflow="hidden" {...badgeProps}>
      <Box flexGrow={1} flexShrink={1} minWidth={0}>
        <BadgeLabel pl={1} label={badgeLabel} threshold={threshold} />
      </Box>
      <Box flexShrink={0} mx="4px">
        <BadgeInfoIcon bg={bg} color={color} tooltipContent={tooltipContent} />
      </Box>
    </Badge>
  );
};
